import {t} from 'i18next';
import React, {ReactElement} from 'react';
import {useSelector} from 'react-redux';
import ElementsToClickBeforeDisplay from './elementsToClickBeforeDisplay';
import ElementsToDisplayAfterClick from './elementToDisplayAfterClick';
import {ISlideOption} from '../../../../../incase-domain/slide';
import {IElementQuestion} from '../../../../../incase-domain/elements/question/question';
import {getElementClickBeforeDisplayOptions, getElementName} from '../../../../../utils/element';
import {IElement} from '../../../../../incase-domain/elements';
import {interactiveWithTryButton} from '../../../../../incase-domain/elements/base/ElementTypes';
import ChecklistTile from '../../../../../incase-domain/components/checkbox/ChecklistTile';
import SectionTitle from '../../../../elements/common/SectionTitle';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../../utils/hooks/slideUpdates/useSetSlideElement';
import {selectedSlideSelector} from '../../../../../store/selectors/slide.selectors';

function ElementsToDisplayBeforeAfter(): ReactElement {
    const highlightedElement = useHighlightedElement() as IElement;
    const {elementToClickForDisplay = [], elementToDisplayAfterClick = [], clearAfterClicked = false} = highlightedElement;
    const disabled = !highlightedElement.id;
    const updateHighlightedElement = useSetHighlightedElement();
    const slide = useSelector(selectedSlideSelector);

    const slideElements = slide?.elements?.filter((slideElement) => slideElement.id && slideElement.id !== highlightedElement.id) || [];

    const isQuestionWithoutTryButton =
        interactiveWithTryButton.includes(highlightedElement.type) && !(highlightedElement as IElementQuestion).haveTryButton;

    // elements to click before display
    const beforeElementOptions: ISlideOption[] = slideElements.reduce((reducedElements: ISlideOption[], slideElement: IElement) => {
        return [...reducedElements, ...getElementClickBeforeDisplayOptions(slideElement)];
    }, []);

    // elements to display after click
    const afterElementOptions: ISlideOption[] = slideElements.map((slideElement) => {
        return {label: getElementName(slideElement), value: slideElement.id};
    });

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.conditionalDisplay')} />
            {disabled && <div className={`${disabled && 'is-error'}`}>{t('designSidepanel.elementsToDisplayDisabledMessage')}</div>}

            <div className="u-flex u-flex-col u-gap-2">
                <ElementsToClickBeforeDisplay
                    disabled={disabled}
                    slideElementOptions={beforeElementOptions}
                    elementToClickForDisplay={elementToClickForDisplay}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />

                <ElementsToDisplayAfterClick
                    disabled={disabled || isQuestionWithoutTryButton}
                    slideElementOptions={afterElementOptions}
                    elementToDisplayAfterClick={elementToDisplayAfterClick}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />

                <div className="u-mt-2">
                    <ChecklistTile
                        value={clearAfterClicked}
                        label={t('designSidepanel.clearAfterClicked')}
                        help={t('designSidepanel.clearAfterClickedExpl')}
                        onChange={(value) => updateHighlightedElement.single('clearAfterClicked', value)}
                    />
                </div>
            </div>
        </div>
    );
}

export default ElementsToDisplayBeforeAfter;
